import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { AlertCircle, Paperclip, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PageContainer } from "@/components/page-container";
import { PageHeader } from "@/components/page-header";
import { TableSkeleton } from "@/components/skeletons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Empty, EmptyDescription, EmptyTitle } from "@/components/ui/empty";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { classificationLabel } from "@/lib/inbound-email";
import { cn } from "@/lib/utils";
import { trpc, withErrorToast } from "@/utils/trpc";

function formatReceived(value: string | Date) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }
  return format(date, "d MMM yyyy, HH:mm");
}

export default function EmailInbox() {
  const navigate = useNavigate();
  const emails = useQuery(
    withErrorToast(trpc.inboundEmails.list.queryOptions())
  );

  return (
    <PageContainer className="space-y-6">
      <PageHeader
        title="Email"
        description="Documents, photos, and messages forwarded into your sites by email."
        toolbar={
          <Button
            variant="outline"
            size="sm"
            onClick={() => emails.refetch()}
            disabled={emails.isFetching}
          >
            <RefreshCw
              className={cn("h-4 w-4", emails.isFetching && "animate-spin")}
            />
            {emails.isFetching ? "Refreshing…" : "Refresh"}
          </Button>
        }
      />

      {emails.isLoading ? (
        <TableSkeleton columns={6} />
      ) : emails.isError ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Failed to load emails</AlertTitle>
          <AlertDescription>
            {emails.error?.message ?? "An error occurred loading your emails."}
          </AlertDescription>
        </Alert>
      ) : emails.data && emails.data.length > 0 ? (
        <div className="surface-panel overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Received</TableHead>
                <TableHead>Site</TableHead>
                <TableHead>From</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Classification</TableHead>
                <TableHead className="text-right">Attachments</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {emails.data.map((email) => {
                const label = classificationLabel(email.classification);
                return (
                  <TableRow
                    key={email.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/email/${email.id}`)}
                  >
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {formatReceived(email.receivedAt)}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-foreground">
                        {email.siteName ?? "Unknown site"}
                      </div>
                      {email.entranceName ? (
                        <div className="text-xs text-muted-foreground">
                          {email.entranceName}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className="max-w-[14rem] truncate">
                      {email.from ?? "Unknown sender"}
                    </TableCell>
                    <TableCell className="max-w-[18rem]">
                      <div className="truncate">
                        {email.subject || (
                          <span className="text-muted-foreground">
                            (no subject)
                          </span>
                        )}
                      </div>
                      {email.summary ? (
                        <div className="truncate text-xs text-muted-foreground">
                          {email.summary}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {label ? (
                        <Badge variant="secondary">{label}</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {email.attachmentCount > 0 ? (
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <Paperclip className="h-3.5 w-3.5" />
                          {email.attachmentCount}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : (
        <Empty className="border-dashed p-8">
          <EmptyTitle>No emails yet</EmptyTitle>
          <EmptyDescription>
            Forwarded documents and messages for your sites will appear here.
            Find each site's intake address on its settings page.
          </EmptyDescription>
        </Empty>
      )}
    </PageContainer>
  );
}
